import React from "react";
import PropTypes from "prop-types";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: "Helvetica",
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 12,
  },
  table: {
    width: "100%",
  },
  headerRow: {
    flexDirection: "row",
    borderBottomWidth: 2,
    borderBottomColor: "#ccc",
    paddingBottom: 4,
    marginBottom: 4,
    fontWeight: "bold",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 3,
  },
  cellSku: { width: "12%" },
  cellBin: { width: "6%" },
  cellTitle: { width: "20%" },
  cellDate: { width: "8%", textAlign: "right" },
  cellAvg: { width: "7%", textAlign: "right" },
  cellOnHand: { width: "7%", textAlign: "right" },
  cellAvailable: { width: "7%", textAlign: "right" },
});

function StockReportPDFDocument({ data }) {
  const dateColumns = data.length ? data[0].salesByDate || [] : [];

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Text style={styles.title}>Stock Report</Text>

        <View style={styles.table}>
          <View style={styles.headerRow}>
            <Text style={styles.cellSku}>SKU</Text>
            <Text style={styles.cellBin}>Bin</Text>
            <Text style={styles.cellTitle}>Title</Text>
            {dateColumns.map((entry) => (
              <Text key={entry.date} style={styles.cellDate}>
                {entry.date}
              </Text>
            ))}
            <Text style={styles.cellAvg}>6W Avg</Text>
            <Text style={styles.cellOnHand}>On Hand</Text>
            <Text style={styles.cellAvailable}>W/Avail</Text>
          </View>

          {data.map((row, idx) => (
            <View key={idx} style={styles.row} wrap={false}>
              <Text style={styles.cellSku}>{row.sku}</Text>
              <Text style={styles.cellBin}>{row.bin}</Text>
              <Text style={styles.cellTitle}>{row.title}</Text>
              {(row.salesByDate || []).map((entry) => (
                <Text key={entry.date} style={styles.cellDate}>
                  {entry.quantity}
                </Text>
              ))}
              <Text style={styles.cellAvg}>{row.average}</Text>
              <Text style={styles.cellOnHand}>{row.onHand}</Text>
              <Text style={styles.cellAvailable}>{row.available}</Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
}

StockReportPDFDocument.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      bin: PropTypes.string,
      title: PropTypes.string,
      average: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      onHand: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      available: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      salesByDate: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          quantity: PropTypes.number,
        })
      ),
    })
  ).isRequired,
};

export function StockReportPDF({ data, children }) {
  if (!data || data.length === 0) {
    return children;
  }

  return (
    <PDFDownloadLink
      document={<StockReportPDFDocument data={data} />}
      fileName="stock-report.pdf"
    >
      {children}
    </PDFDownloadLink>
  );
}

StockReportPDF.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  children: PropTypes.node.isRequired,
};

export default StockReportPDF;
